import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { User } from '@litellm-ui/shared';

export type { User };

export type UserRole =
  | 'proxy_admin'
  | 'proxy_admin_viewer'
  | 'internal_user'
  | 'internal_user_viewer';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api<User[]>('/api/users'),
  });
}

export function useUserInfo(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-info', userId],
    queryFn: () =>
      api<User>('/api/users/info', {
        query: { user_id: userId },
      }),
    enabled: !!userId,
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) =>
      api('/api/users/delete', { method: 'POST', body: { user_ids: userIds } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      user_email: string;
      user_alias?: string;
      user_role?: UserRole;
      teams?: string[];
      max_budget?: number;
      models?: string[];
      metadata?: Record<string, unknown>;
    }) => api('/api/users/new', { method: 'POST', body: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      user_id: string;
      user_email?: string;
      user_alias?: string;
      user_role?: UserRole;
      teams?: string[];
      max_budget?: number;
      models?: string[];
      metadata?: Record<string, unknown>;
    }) => api('/api/users/update', { method: 'POST', body: data }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['user-info', vars.user_id] });
    },
  });
}